import type { AppContext } from '../app';
import type { NewsHeadlineRecord } from '../storage/schema';

/**
 * Phase 69.A — news headlines manager (client side).
 *
 * Owns the `news_headlines` Dexie table and serves the NewsTicker
 * marquee + `news-paper` surface. For now headlines are local-only +
 * operator-curated via the news-paper publish form. Phase 69.B will
 * add a VPS-side RSS aggregator that publishes signed `news-headline`
 * envelopes — those get ingested via `ingestFromEnvelope()` here
 * (stubbed; landing in the same patch as 69.B).
 *
 * Mirrors the singleton-per-AppContext pattern used by
 * PersonalHelper / MascotManager / PresenceManager.
 */

export interface PublishHeadlineInput {
  title: string;
  source: string;
  url?: string;
  summary?: string;
}

const NEWS_HEADLINES_KEY = Symbol.for('alds.newsHeadlinesManager');

export class NewsHeadlinesManager {
  readonly #ctx: AppContext;
  readonly #activeSubs = new Set<(rows: readonly NewsHeadlineRecord[]) => void>();
  readonly #allSubs = new Set<(rows: readonly NewsHeadlineRecord[]) => void>();

  constructor(ctx: AppContext) {
    this.#ctx = ctx;
  }

  /**
   * Publish a new headline locally. Auto-generates id + publishedAt.
   * Notifies subscribers.
   */
  async publish(input: PublishHeadlineInput): Promise<NewsHeadlineRecord> {
    const trimmedTitle = input.title.trim();
    if (trimmedTitle === '') throw new Error('NewsHeadlinesManager.publish: title is required');
    const trimmedSource = input.source.trim();
    if (trimmedSource === '') throw new Error('NewsHeadlinesManager.publish: source is required');
    const record: NewsHeadlineRecord = {
      id: makeId(),
      title: trimmedTitle,
      source: trimmedSource,
      publishedAt: new Date().toISOString(),
      dismissedAt: null,
    };
    if (input.url !== undefined && input.url.trim() !== '') record.url = input.url.trim();
    if (input.summary !== undefined && input.summary.trim() !== '') record.summary = input.summary.trim();
    await this.#ctx.storage.db.news_headlines.put(record);
    await this.#emitAll();
    return record;
  }

  /**
   * Ingest a headline published by a federated source (Phase 69.B
   * RSS aggregator daemon, or a future BOO.ai-approved peer feed).
   * Caller is responsible for signature verification BEFORE calling
   * this — the manager assumes the record is already trusted.
   */
  async ingestFromEnvelope(record: NewsHeadlineRecord): Promise<void> {
    await this.#ctx.storage.db.news_headlines.put(record);
    await this.#emitAll();
  }

  /** Active = not dismissed + ordered newest first. Caps at `limit`. */
  async listActive(limit = 12): Promise<NewsHeadlineRecord[]> {
    const all = await this.#ctx.storage.db.news_headlines
      .orderBy('publishedAt')
      .reverse()
      .toArray();
    return all.filter((r) => r.dismissedAt === null).slice(0, limit);
  }

  /** All headlines (including dismissed) newest first; for the news-paper surface. */
  async listAll(limit = 60): Promise<NewsHeadlineRecord[]> {
    const all = await this.#ctx.storage.db.news_headlines
      .orderBy('publishedAt')
      .reverse()
      .toArray();
    return all.slice(0, limit);
  }

  /** Mark a headline as dismissed. Idempotent. Notifies subscribers. */
  async dismiss(headlineId: string): Promise<void> {
    const existing = await this.#ctx.storage.db.news_headlines.get(headlineId);
    if (existing === undefined || existing.dismissedAt !== null) return;
    await this.#ctx.storage.db.news_headlines.put({
      ...existing,
      dismissedAt: new Date().toISOString(),
    });
    await this.#emitAll();
  }

  /** Un-dismiss; helpful for the news-paper view. */
  async restore(headlineId: string): Promise<void> {
    const existing = await this.#ctx.storage.db.news_headlines.get(headlineId);
    if (existing === undefined || existing.dismissedAt === null) return;
    await this.#ctx.storage.db.news_headlines.put({ ...existing, dismissedAt: null });
    await this.#emitAll();
  }

  /** Remove a headline permanently. */
  async remove(headlineId: string): Promise<void> {
    await this.#ctx.storage.db.news_headlines.delete(headlineId);
    await this.#emitAll();
  }

  /**
   * Subscribe to changes in the active (non-dismissed) headline list.
   * Fires immediately with the current snapshot.
   */
  onActiveChange(fn: (rows: readonly NewsHeadlineRecord[]) => void): () => void {
    this.#activeSubs.add(fn);
    void this.listActive().then((rows) => fn(rows));
    return () => this.#activeSubs.delete(fn);
  }

  /**
   * Subscribe to changes in the full list (incl. dismissed).
   * Fires immediately with the current snapshot.
   */
  onAllChange(fn: (rows: readonly NewsHeadlineRecord[]) => void): () => void {
    this.#allSubs.add(fn);
    void this.listAll().then((rows) => fn(rows));
    return () => this.#allSubs.delete(fn);
  }

  async #emitAll(): Promise<void> {
    const [active, all] = await Promise.all([this.listActive(), this.listAll()]);
    for (const fn of this.#activeSubs) fn(active);
    for (const fn of this.#allSubs) fn(all);
  }
}

function makeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `nh_${crypto.randomUUID()}`;
  }
  return `nh_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Per-AppContext singleton accessor. */
export function getNewsHeadlinesManager(ctx: AppContext): NewsHeadlinesManager {
  const bag = ctx as unknown as Record<symbol, NewsHeadlinesManager | undefined>;
  let manager = bag[NEWS_HEADLINES_KEY];
  if (manager === undefined) {
    manager = new NewsHeadlinesManager(ctx);
    bag[NEWS_HEADLINES_KEY] = manager;
  }
  return manager;
}
